import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { FixedSizeList, ListChildComponentProps } from 'react-window';

import {
  ZfBox,
  ZfDivider,
  ZfFormField,
  ZfIcon,
  ZfInput,
  ZfLink,
  ZfListHeader,
  ZfPopover,
  ZfRadio,
  ZfSelectionControlGroup,
  ZfSelectOption,
  ZfStack,
  ZfTypography,
} from '@zafire/react';
import { useFormikContext } from 'formik';
import { useCaretResetOnFocus } from 'hooks/useCaretResetOnFocus';
import { GlobalState } from 'reducers/stateTypes';
import contactNoteDialogService, { QualifiedStatusResponse } from 'services/contactNoteDialogService';
import { DropdownOptionActiveState } from 'components/Common/ValidatedDropdown';
import { ContactNoteDialog } from 'types/ContactNote';
import { DOCUMENT_JBG_2082_00 } from 'services/constants';

import { CommunicationChannelCountryShowList, CommunicationChannelEndDateList } from './data/DialogOptions';
import { ContactType, isActiveType, SelectOption } from './data/ContactNoteDialogOptions';

const DROPDOWN_HEIGHT = 32;
type RenderItem =
  | { type: 'header'; category: string }
  | { type: 'option'; value: string; label: string }
  | { type: 'divider' };
const ContactDetails = ({
  nonEditable = false,
  edit,
  isFromEmail = false,
  isDragAndDrop = false,
}: {
  nonEditable: boolean;
  edit: boolean;
  isFromEmail?: boolean;
  isDragAndDrop?: boolean;
}) => {
  const { t } = useTranslation();
  //The formik context is using ContactNoteDialog until submission will convert to ContactNote to use existing api for current phase
  const { getFieldProps, values, errors, touched, setFieldValue, setFieldTouched, setValues } =
    useFormikContext<ContactNoteDialog>();
  const [initiatedBySuggestion, setInitiatedBySuggestion] = useState('');
  const [contactTypeSuggestion, setContactTypeSuggestion] = useState('');
  const [topicSuggestion, setTopicSuggestion] = useState('');
  const [countrySuggestion, setCountrySuggestion] = useState('');

  const initiatedByOptions = useSelector((state: GlobalState) => state.resources.initiatedByOptions);
  const topicOptions = useSelector((state: GlobalState) => state.resources.topicsOptions);
  const contactTypeOptions = useSelector((state: GlobalState) => state.resources.contactTypeOptions);
  const countryOptions = useSelector((state: GlobalState) => state.resources.countries);
  const user = useSelector((state: GlobalState) => state.user.userDetails);

  const myTravelNonEditableField = useMemo(() => {
    return values?.source === 'MYTRAVEL' && edit;
  }, [values?.source, edit]);

  const [disableQualifiedContact, setDisableQualifiedContact] = useState(false);

  const filterListBySuggestion = useCallback((list: SelectOption[], suggestion: string): SelectOption[] => {
    const lowerCaseSuggestion = suggestion.toLowerCase();
    return list.filter((option) => option?.label?.toLowerCase().includes(lowerCaseSuggestion));
  }, []);

  const initiatedByFilteredOptions = useMemo(() => {
    if (initiatedByOptions?.length === 0) {
      return [];
    }
    const list = initiatedByOptions.filter(
      (item) => item.isActive === DropdownOptionActiveState.YES || values.initiatedBy === item.value,
    );
    if (values.initiatedBy && !list.some((option) => option.value === values.initiatedBy)) {
      list.push({ label: values.initiatedBy, value: values.initiatedBy });
    }
    return filterListBySuggestion(list, initiatedBySuggestion);
  }, [initiatedByOptions, initiatedBySuggestion, values.initiatedBy]);

  const contactTypeFilteredOptions = useMemo(() => {
    if (contactTypeOptions?.length === 0) {
      return [];
    }
    const list = contactTypeOptions.filter(
      (item) => item.isActive === DropdownOptionActiveState.YES || values.contactType === item.value,
    );
    if (values.contactType && !list.some((option) => option.value === values.contactType)) {
      list.push({ label: values.contactType, value: values.contactType });
    }
    return filterListBySuggestion(list, contactTypeSuggestion);
  }, [contactTypeOptions, contactTypeSuggestion, values.contactType]);

  const countryFilteredOptions = useMemo(() => {
    return filterListBySuggestion(countryOptions, countrySuggestion);
  }, [countryOptions, countrySuggestion]);

  const showEndDate = useMemo(() => {
    return CommunicationChannelEndDateList.includes(values.contactType as ContactType);
  }, [values.contactType]);

  const showLocation = useMemo(() => {
    return CommunicationChannelCountryShowList.includes(values.contactType as ContactType);
  }, [values.contactType]);

  const groupedTopicOptions = useMemo(() => {
    if (topicOptions?.length === 0) return [];
    const list = topicOptions.filter(
      (item) => item.isActive === DropdownOptionActiveState.YES || values.topics.includes(item.value),
    );
    const valueList = new Set(list.map((item) => item.value));
    const additionalItems = values.topics.filter((item) => !valueList.has(item));
    if (additionalItems.length > 0) {
      for (const str of additionalItems) {
        list.push({ value: str, label: str, category: 'Other' });
      }
    }
    const topicList = filterListBySuggestion(list, topicSuggestion);
    return Object.entries(
      topicList.reduce(
        (
          acc: { [category: string]: { value: string; label: string; category: string; isActive?: isActiveType }[] },
          option,
        ) => {
          const category = option.category as string;
          acc[category] = acc[category] || [];
          acc[category].push({ value: option.value, label: option.label, category, isActive: option?.isActive });
          return acc;
        },
        {},
      ),
    ).map(([category, options]) => [{ category, value: '', label: '' }, ...options]);
  }, [topicOptions, topicSuggestion, values.topics]);
  const flatOptions = useMemo(() => {
    const flatOptions: RenderItem[] = [];

    groupedTopicOptions.forEach((list) => {
      if (list.length > 0) {
        flatOptions.push({ type: 'header', category: list[0].category });
        list.forEach((item, index) => {
          if (index !== 0) {
            flatOptions.push({ type: 'option', value: item.value, label: item.label });
          }
        });
      }
    });
    return flatOptions;
  }, [groupedTopicOptions]);
  const Row = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    const item = flatOptions[index];

    if (item.type === 'header') {
      return <ZfListHeader style={style} size="small" description={item.category} />;
    }

    if (item.type === 'option') {
      if (flatOptions.length > index + 1 && flatOptions[index + 1]?.type === 'header') {
        return (
          <div style={style} key={`${item.value}_${item.label}`}>
            <ZfSelectOption value={item.value} isSelected={values.topics?.includes(item.value)}>
              {item.label}
            </ZfSelectOption>
            <ZfDivider />
          </div>
        );
      }
      return (
        <ZfSelectOption style={style} value={item.value} isSelected={values.topics?.includes(item.value)}>
          {item.label}
        </ZfSelectOption>
      );
    }

    return null;
  };

  const isTopicIncludingOther = useMemo(() => {
    return values.topics?.includes('OTHER');
  }, [values.topics]);

  useEffect(() => {
    if (edit && values.locationCountry && countryOptions.length > 0 && !user.hasNoteDeleteAccess) {
      const country = countryOptions.find((item) => item.value === values.locationCountry);
      if (!country) {
        setFieldValue('locationCountry', '');
      }
    }
  }, [edit, values.locationCountry, countryOptions, user.hasNoteDeleteAccess]);

  useEffect(() => {
    async function fetchQualifiedContact(contactType: string, initiatedBy: string) {
      try {
        const data: QualifiedStatusResponse = await contactNoteDialogService.getQualifiedContact(
          contactType,
          initiatedBy,
        );
        if (data?.status === 'N/A') {
          setDisableQualifiedContact(false);
          if (!edit) {
            setFieldValue('qualifiedClientContact', '');
          }
        } else {
          setDisableQualifiedContact(true);
          setFieldValue('qualifiedClientContact', data?.status.toString());
        }
      } catch {
        setDisableQualifiedContact(false);
        // setFeedbackMessage('Could not log in: ' + (error && error.response ? error.response.statusText : error));
      }
      return;
    }

    if (user.hasUpdateQualifiedContact) {
      setDisableQualifiedContact(false);
    } else if (values.initiatedBy && values.contactType) {
      fetchQualifiedContact(values.contactType, values.initiatedBy);
    } else if (disableQualifiedContact) {
      setDisableQualifiedContact(false);
    }
  }, [values.initiatedBy, values.contactType, user.hasUpdateQualifiedContact, edit]);

  const timeFieldCaretReset = useCaretResetOnFocus();
  const dateFieldCaretReset = useCaretResetOnFocus();

  return (
    <>
      <ZfStack direction="row" spacing="small" style={{ paddingBottom: 'var(--gap-vertical-md)' }}>
        <ZfTypography variant="label-medium" color="emphasis" weight="bold">
          {t('contactNote.form.contactDetail')}
        </ZfTypography>
        <ZfDivider style={{ flex: 1, paddingTop: 'var(--padding-top-sm)' }} />
      </ZfStack>
      <ZfStack direction="row" spacing="small">
        {/* communicationChannel is referecing to existing api contactType parameter*/}
        <ZfFormField
          style={{ flex: 1, minHeight: 78 }}
          label={t('contactNote.form.communicationChannel')}
          mandatory
          alerts={[errors.contactType ?? '']}
        >
          <ZfInput
            name="contactType"
            type="select"
            onSelectChange={(e) => {
              if (e.detail !== values.contactType) {
                setFieldValue('locationCountry', '');
                setFieldValue('locationCity', '');
                setFieldValue('tripId', '');
                setFieldValue('startDate', '');
                setFieldValue('startTime', '');
                setFieldValue('endDate', '');
                setFieldValue('endTime', '');
              }
              setFieldValue('contactType', e.detail);
            }}
            onInputDropdownOpen={() => setContactTypeSuggestion('')}
            onInputChange={(e) => setContactTypeSuggestion(e.detail)}
            placeholder={t('contactNote.form.placeholder.selectOrEnter', {
              name: t('contactNote.form.placeholder.label.communicationChannel'),
            })}
            allowSearch
            error={!!errors.contactType && touched.contactType}
            data-testid="contactType-content-input"
            id="contactType-content-input"
            disabled={nonEditable || isFromEmail || (edit && isDragAndDrop) || myTravelNonEditableField}
            value={values.contactType}
          >
            <FixedSizeList
              itemSize={DROPDOWN_HEIGHT}
              itemCount={contactTypeFilteredOptions.length}
              overscanCount={5}
              height={Math.min(contactTypeFilteredOptions.length, 10) * DROPDOWN_HEIGHT}
              width="100%"
            >
              {(props: ListChildComponentProps) => {
                const { index, style } = props;
                const co = contactTypeFilteredOptions[index];

                return (
                  <ZfSelectOption
                    style={style}
                    key={co.value}
                    value={co.value}
                    isSelected={values.contactType?.includes(co.value)}
                  >
                    {co.label}
                  </ZfSelectOption>
                );
              }}
            </FixedSizeList>
          </ZfInput>
          <ZfPopover slot="label-trailing" alignment="start" data-testid="communication-channel-popover">
            <ZfIcon slot="anchor" size="medium" name="info" style={{ cursor: 'pointer' }}></ZfIcon>
            <ZfBox styles={{ textWrap: 'wrap' }}>
              <ZfTypography variant="label-tiny" color="normal">
                <Trans
                  i18nKey="contactNote.form.helperText.communicationChannel"
                  components={{
                    1: (
                      <ZfLink
                        size="extra-small"
                        type="inline"
                        target="_blank"
                        rel="noreferrer"
                        href={DOCUMENT_JBG_2082_00}
                      />
                    ),
                  }}
                />
              </ZfTypography>
            </ZfBox>
          </ZfPopover>
        </ZfFormField>
        <ZfFormField
          style={{ flex: 1, minHeight: 78 }}
          label={t('contactNote.form.topics')}
          mandatory
          alerts={[errors.topics?.toString() ?? '']}
        >
          <ZfInput
            type="combo-box"
            placeholder={t('contactNote.form.placeholder.selectOrEnter', {
              name: t('contactNote.form.placeholder.label.topics'),
            })}
            allowSearch
            onInputDropdownOpen={() => setTopicSuggestion('')}
            onInputChange={(e) => setTopicSuggestion(e.detail)}
            name="topics"
            disabled={nonEditable || myTravelNonEditableField}
            onSelectChange={(e) => setFieldValue('topics', e.detail)}
            error={!!errors.topics && touched.topics}
            data-testid="topics-content-input"
            id="topics-content-input"
            value={values.topics}
            getOptionLabel={(option) => topicOptions.find((co) => co.value === option)?.label || option}
          >
            <FixedSizeList
              height={Math.min(flatOptions.length, 10) * DROPDOWN_HEIGHT}
              itemCount={flatOptions.length}
              itemSize={DROPDOWN_HEIGHT}
              width="100%"
            >
              {Row}
            </FixedSizeList>
          </ZfInput>
        </ZfFormField>
      </ZfStack>
      {(values.contactType === ContactType.OTHER || isTopicIncludingOther) && (
        <ZfStack direction="row" spacing="small">
          {values.contactType === ContactType.OTHER ? (
            <ZfFormField
              style={{ flex: 1, minHeight: 78 }}
              label={t('contactNote.form.communicationChannelOther')}
              mandatory
              alerts={[errors.contactTypeOther ?? '']}
            >
              <ZfInput
                {...getFieldProps('contactTypeOther')}
                placeholder={t('contactNote.form.placeholder.label.otherCommunicationChannel')}
                name="contactTypeOther"
                onInputChange={getFieldProps('contactTypeOther').onChange}
                maxLength={200}
                counter
                disabled={nonEditable || myTravelNonEditableField}
                error={!!errors.contactTypeOther && touched.contactTypeOther}
                data-testid="contactTypeOther-content-input"
              />
            </ZfFormField>
          ) : (
            <ZfBox styles={{ flex: '1' }}></ZfBox>
          )}
          {isTopicIncludingOther ? (
            <ZfFormField
              style={{ flex: 1, minHeight: 78 }}
              label={t('contactNote.form.topicsOther')}
              mandatory
              alerts={[errors.topicOther ?? '']}
            >
              <ZfInput
                {...getFieldProps('topicOther')}
                placeholder={t('contactNote.form.placeholder.label.otherTopic')}
                name="topicOther"
                onInputChange={getFieldProps('topicOther').onChange}
                disabled={nonEditable}
                maxLength={200}
                counter
                error={!!errors.topicOther && touched.topicOther}
                data-testid="topicOther-content-input"
              />
            </ZfFormField>
          ) : (
            <ZfBox styles={{ flex: '1' }}></ZfBox>
          )}
        </ZfStack>
      )}
      {values.contactType !== '' && !showEndDate && (
        <ZfStack direction="row" spacing="small">
          <ZfFormField
            mandatory
            label={t('contactNote.form.date')}
            style={{ flex: 1, textWrap: 'wrap', minHeight: 78 }}
            alerts={[errors.startDate ?? '']}
          >
            <ZfInput
              type="date"
              value={values.startDate}
              onInputChange={(e) => {
                setFieldValue('startDate', e.detail);
              }}
              {...dateFieldCaretReset}
              style={{ flex: 1, textWrap: 'wrap' }}
              disabled={nonEditable}
              error={!!(errors.startDate && touched.startDate)}
              data-testid="startDateTime-dateTimePicker-field"
              id="startDateTime-dateTimePicker-field"
            />
          </ZfFormField>

          <ZfFormField
            label={t('contactNote.form.time')}
            style={{ flex: 1, textWrap: 'wrap', minHeight: 78 }}
            alerts={[errors.startTime ?? '']}
          >
            <ZfInput
              type="time"
              value={values.startTime}
              onInputChange={(e) => {
                setFieldValue('startTime', e.detail);
              }}
              {...timeFieldCaretReset}
              placeholder={t('contactNote.form.placeholder.label.time')}
              disabled={nonEditable}
              error={!!(errors.startTime && touched.startTime)}
              data-testid="startTime-timePicker-field"
              id="startTime-timePicker-field"
            />
          </ZfFormField>
        </ZfStack>
      )}
      {values.contactType !== '' && showEndDate && (
        <ZfStack direction="row" spacing="small">
          <ZfFormField
            mandatory
            label={t('contactNote.form.startDate')}
            style={{ flex: 1, textWrap: 'wrap', minHeight: 78 }}
            alerts={[errors.startDate ?? '']}
          >
            <ZfInput
              type="date"
              value={values.startDate}
              onInputChange={(e) => {
                setValues({ ...values, startDate: e.detail, endDate: e.detail });
                setFieldTouched('endDate', false);
              }}
              {...dateFieldCaretReset}
              disabled={nonEditable}
              error={!!(errors.startDate && touched.startDate)}
              data-testid="startDateTime-dateTimePicker-field"
              id="startDateTime-dateTimePicker-field"
            />
          </ZfFormField>
          <ZfFormField
            mandatory
            label={t('contactNote.form.startTime')}
            style={{ flex: 1, textWrap: 'wrap', minHeight: 78 }}
            alerts={[errors.startTime ?? '']}
          >
            <ZfInput
              type="time"
              value={values.startTime}
              onInputChange={(e) => {
                setFieldValue('startTime', e.detail);
              }}
              {...timeFieldCaretReset}
              placeholder={t('contactNote.form.placeholder.label.time')}
              disabled={nonEditable}
              error={!!(errors.startTime && touched.startTime)}
              data-testid="startTime-timePicker-field"
              id="startTime-timePicker-field"
            />
          </ZfFormField>

          <ZfFormField
            mandatory
            label={t('contactNote.form.endDate')}
            style={{ flex: 1, textWrap: 'wrap', minHeight: 78 }}
            alerts={[errors.endDate ?? '']}
          >
            <ZfInput
              type="date"
              value={values.endDate}
              onInputChange={(e) => {
                setFieldValue('endDate', e.detail);
                setTimeout(() => {
                  setFieldTouched('startDate', true);
                  setFieldTouched('endDate', true, false);
                }, 0);
              }}
              {...dateFieldCaretReset}
              disabled={nonEditable}
              error={!!(errors.endDate && touched.endDate)}
              data-testid="endDateTime-dateTimePicker-field"
              id="endDateTime-dateTimePicker-field"
            />
          </ZfFormField>
          <ZfFormField
            mandatory
            label={t('contactNote.form.endTime')}
            style={{ flex: 1, textWrap: 'wrap', minHeight: 78 }}
            alerts={[errors.endTime ?? '']}
          >
            <ZfInput
              type="time"
              value={values.endTime}
              onInputChange={(e) => {
                setFieldValue('endTime', e.detail);
              }}
              {...timeFieldCaretReset}
              placeholder={t('contactNote.form.placeholder.label.time')}
              disabled={nonEditable}
              error={!!(errors.endTime && touched.endTime)}
              data-testid="endTime-timePicker-field"
              id="endTime-timePicker-field"
            />
          </ZfFormField>
        </ZfStack>
      )}

      <ZfStack direction="row" spacing="small">
        <ZfFormField
          style={{ flex: 2, minHeight: 78 }}
          label={t('contactNote.form.initiatedBy')}
          mandatory
          alerts={[errors.initiatedBy?.toString() ?? '']}
        >
          <ZfInput
            placeholder={t('contactNote.form.placeholder.selectOrEnter', {
              name: t('contactNote.form.placeholder.label.role'),
            })}
            value={values.initiatedBy}
            type="select"
            allowSearch
            onSelectChange={(e) => {
              setFieldValue('initiatedBy', e.detail);
            }}
            onInputDropdownOpen={() => setInitiatedBySuggestion('')}
            onInputChange={(e) => setInitiatedBySuggestion(e.detail)}
            error={!!errors.initiatedBy && touched.initiatedBy}
            data-testid="initiatedBy-content-input"
            id="initiatedBy-content-input"
            disabled={nonEditable}
            getOptionLabel={(option) => initiatedByOptions.find((co) => co.value === option)?.label || option}
          >
            <FixedSizeList
              itemSize={DROPDOWN_HEIGHT}
              itemCount={initiatedByFilteredOptions.length}
              overscanCount={5}
              height={Math.min(initiatedByFilteredOptions.length, 10) * DROPDOWN_HEIGHT}
              width="100%"
            >
              {(props: ListChildComponentProps) => {
                const { index, style } = props;
                const co = initiatedByFilteredOptions[index];

                return (
                  <ZfSelectOption
                    style={style}
                    key={co.value}
                    value={co.value}
                    isSelected={values.initiatedBy?.includes(co.value)}
                  >
                    {co.label}
                  </ZfSelectOption>
                );
              }}
            </FixedSizeList>
          </ZfInput>
        </ZfFormField>
        <ZfStack direction="row" spacing="small" style={{ flex: 2 }}>
          <ZfSelectionControlGroup
            {...getFieldProps('qualifiedClientContact')}
            selectionType="radio"
            value={values.qualifiedClientContact ? values.qualifiedClientContact.toString() : undefined}
            onGroupValueChange={(e) => {
              setFieldValue('qualifiedClientContact', e.detail);
            }}
            error={!!errors.qualifiedClientContact && touched.qualifiedClientContact}
            errorText={errors.qualifiedClientContact}
            label={t('contactNote.form.qualifiedClientContact')}
            style={{ flex: 1, textWrap: 'wrap' }}
            mandatory
            data-testid="qualifiedClientContact"
          >
            <ZfStack direction="row" spacing="small">
              <ZfRadio
                disabled={disableQualifiedContact || (nonEditable && !user.hasUpdateQualifiedContact)}
                value="true"
                label="Yes"
              ></ZfRadio>
              <ZfRadio
                disabled={disableQualifiedContact || (nonEditable && !user.hasUpdateQualifiedContact)}
                value="false"
                label="No"
              ></ZfRadio>
            </ZfStack>
          </ZfSelectionControlGroup>

          <ZfSelectionControlGroup
            {...getFieldProps('crossBorderActivity')}
            selectionType="radio"
            value={values.crossBorderActivity ? values.crossBorderActivity.toString() : undefined}
            onGroupValueChange={(e) => {
              if (e.detail !== values?.crossBorderActivity?.toString()) {
                setFieldValue('locationCountry', '');
                setFieldValue('locationCity', '');
                setFieldValue('tripId', '');
              }
              setFieldValue('crossBorderActivity', e.detail);
            }}
            style={{ flex: 1, textWrap: 'wrap' }}
            label={t('contactNote.form.crossBorderActivity')}
            mandatory
            error={!!errors.crossBorderActivity && touched.crossBorderActivity}
            errorText={errors.crossBorderActivity}
            data-testid="crossBorderActivity"
          >
            <ZfPopover alignment="start" slot="label-trailing">
              <ZfIcon slot="anchor" size="medium" name="info" style={{ cursor: 'pointer' }}></ZfIcon>
              <ZfBox>
                <ZfTypography variant="label-tiny" color="normal" style={{ whiteSpace: 'pre-line' }}>
                  {t('contactNote.form.crossBorderActivityPopover', { interpolation: { escapeValue: false } })}
                </ZfTypography>
              </ZfBox>
            </ZfPopover>

            <ZfStack direction="row" spacing="small">
              <ZfRadio value="true" label="Yes" disabled={nonEditable || myTravelNonEditableField}></ZfRadio>
              <ZfRadio value="false" label="No" disabled={nonEditable || myTravelNonEditableField}></ZfRadio>
            </ZfStack>
          </ZfSelectionControlGroup>
        </ZfStack>
      </ZfStack>

      {(showLocation || values.crossBorderActivity?.toString() === 'true') && (
        <ZfStack direction="row" spacing="small">
          <ZfFormField
            style={{ flex: 2, minHeight: 78 }}
            label={t('contactNote.form.locationCountry')}
            mandatory={values.crossBorderActivity?.toString() === 'true'}
            alerts={[errors.locationCountry?.toString() ?? '']}
          >
            <ZfInput
              placeholder={t('contactNote.form.placeholder.selectOrEnter', {
                name: t('contactNote.form.placeholder.label.country'),
              })}
              type="select"
              data-testid="locationCountry"
              allowSearch
              onInputDropdownOpen={() => setCountrySuggestion('')}
              onSelectChange={(e) => setFieldValue('locationCountry', e.detail)}
              onInputChange={(e) => setCountrySuggestion(e.detail)}
              error={!!errors.locationCountry && touched.locationCountry}
              disabled={nonEditable || myTravelNonEditableField}
              value={values.locationCountry}
              getOptionLabel={(option) => countryOptions.find((co) => co.value === option)?.label || ''}
            >
              <FixedSizeList
                itemSize={DROPDOWN_HEIGHT}
                itemCount={countryFilteredOptions.length}
                overscanCount={5}
                height={Math.min(countryFilteredOptions.length, 10) * DROPDOWN_HEIGHT}
                width="100%"
              >
                {(props: ListChildComponentProps) => {
                  const { index, style } = props;
                  const co = countryFilteredOptions[index];

                  return (
                    <ZfSelectOption
                      style={style}
                      key={co.value}
                      value={co.value}
                      disabled={co.label.toLowerCase().includes('invalid')}
                      isSelected={values.locationCountry?.includes(co.value)}
                    >
                      {co.label}
                    </ZfSelectOption>
                  );
                }}
              </FixedSizeList>
            </ZfInput>
          </ZfFormField>
          <ZfFormField style={{ flex: showLocation ? 1 : 2, minHeight: 78 }} label={t('contactNote.form.locationCity')}>
            <ZfInput
              {...getFieldProps('locationCity')}
              placeholder={t('contactNote.form.placeholder.enter', {
                name: t('contactNote.form.placeholder.label.city'),
                interpolation: { escapeValue: false },
              })}
              onInputChange={getFieldProps('locationCity').onChange}
              disabled={nonEditable}
              maxLength={50}
              data-testid="locationCity-content-input"
            />
          </ZfFormField>
          {showLocation && (
            <ZfFormField style={{ flex: 1, minHeight: 78 }} label={t('contactNote.form.tripId')}>
              <ZfInput
                {...getFieldProps('tripId')}
                placeholder={t('contactNote.form.placeholder.enter', {
                  name: t('contactNote.form.placeholder.label.tripId'),
                })}
                onInputChange={getFieldProps('tripId').onChange}
                disabled={nonEditable || myTravelNonEditableField}
                maxLength={200}
                data-testid="tripId-content-input"
              />
            </ZfFormField>
          )}
        </ZfStack>
      )}
    </>
  );
};

export default ContactDetails;
